import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {List} from 'immutable';
import {isNumber} from 'lodash';
import StaticField from 'generic/Forms/StaticField/StaticField.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {setUpStore} from 'client/framework';
import PassageCorrectionQuestionStore from './PassageCorrectionQuestion.store';
import PassageCorrectionPopover from '../shared/PassageCorrectionPopover.react';
import {questionPropType} from 'resources/Question/Question.model';
import './passage-correction-question.scss';

export default class PassageCorrectionPostSubmissionRenderer extends React.Component {
  static propTypes = {
    storeName: PropTypes.string,
    questionId: PropTypes.string,
    question: questionPropType,
    onRenderWordFunc: PropTypes.func
  };

  state = {
    activeIndex: null
  };

  getStore() {
    return setUpStore(PassageCorrectionQuestionStore, this.props.storeName, this.props.questionId);
  }

  handleClick = (e) => {
    const regionIndex = e.target.dataset.regionIndex;
    if (regionIndex === undefined) {
      return;
    }
    this.setState({
      activeIndex: parseInt(e.target.dataset.idx, 10)
    });
  };

  handleActiveIndexReset = () => {
    this.setState({
      activeIndex: null
    });
  };

  wordPropsFunc = (text, optionIndex) => {
    const question = this.props.question;
    const validResponse = question.getValidResponse();
    const matchedRegionIndex = question.getRegionKeyForOptionIndex(optionIndex);
    const baseWordProps = {
      text,
      onRenderWordFunc: this.props.onRenderWordFunc
    };
    if (!matchedRegionIndex) {
      return baseWordProps;
    }
    const matchedRegion = validResponse.get(matchedRegionIndex);
    const regionSolutions = matchedRegion.get('solutions');
    const studentRegionCorrection = this.getStore().getCorrectionAsJoinedStrings(
      matchedRegionIndex
    );
    const hasStudentRegionCorrection = studentRegionCorrection !== null;
    const isDistractor = question.isRegionDistractor(matchedRegionIndex);
    const isCorrect =
      (isDistractor &&
        hasStudentRegionCorrection === false &&
        Boolean(matchedRegion.get('annotation'))) ||
      (!isDistractor && regionSolutions.some((solution) => solution === studentRegionCorrection));
    const isIncorrect = isDistractor
      ? hasStudentRegionCorrection
      : !hasStudentRegionCorrection ||
        regionSolutions.every((solution) => solution !== studentRegionCorrection);
    // Need to explicitly check for false here. Can not just negate the values, as that would cause undefined
    // to pass the conditions.
    const onClick =
      isCorrect === false && isIncorrect === false
        ? (e) => {
            e.preventDefault();
            e.stopPropagation();
          }
        : null;
    const activeIndex = this.state.activeIndex;
    const matchedStartIndex = matchedRegion.get('start_index');
    const matchedEndIndex = matchedRegion.get('end_index');
    const isActive =
      isNumber(activeIndex) && question.doesRegionContainOptionIndex(matchedRegion, activeIndex);
    const isRangeStart = matchedStartIndex === optionIndex;
    const isRangeEnd = matchedEndIndex === optionIndex;
    let correctedText = text;
    if (studentRegionCorrection) {
      const splitStudentCorrection = studentRegionCorrection.split(/\s+/);
      const regionIndexDifference = matchedEndIndex - matchedStartIndex;
      const currentIndex = Math.abs(matchedEndIndex - optionIndex - regionIndexDifference);
      /**
       * A solution can have as many or more words than the incorrect region (i.e. 'alot' can be corrected to 'a lot',
       * but not the other way around). Because of this, if we're rendering the last word in an incorrect region, we
       * render the rest of the words in the student's correction, joined by a space.
       */
      correctedText =
        currentIndex === regionIndexDifference
          ? splitStudentCorrection.slice(regionIndexDifference).join(' ')
          : splitStudentCorrection[currentIndex];
    }

    return {
      ...baseWordProps,
      isActive,
      isCorrect,
      isIncorrect,
      matchedRegionIndex,
      isRangeStart,
      isRangeEnd,
      correctedText,
      onClick
    };
  };

  render() {
    const question = this.props.question;
    const activeIndex = this.state.activeIndex;
    let studentGuess;
    let correctAnswer;
    let annotation;
    if (activeIndex !== null) {
      const store = this.getStore();
      const validResponse = question.getValidResponse();
      const activeRegionKey = question.getRegionKeyForOptionIndex(activeIndex);
      const regionSolutions = question.isRegionDistractor(activeRegionKey)
        ? List.of(store.getInitialCorrectionAsJoinedString(activeRegionKey))
        : question.getSolutionsForRegionKey(activeRegionKey);

      studentGuess = store.responseGuess.getIn(['content', activeRegionKey]);
      correctAnswer = regionSolutions.includes(studentGuess)
        ? studentGuess
        : regionSolutions.first();
      annotation = validResponse.getIn([activeRegionKey, 'annotation']);
    }
    return (
      <MarkdownRendererV2
        passageCorrection
        className='passage-correction-question__input-area'
        text={question.getUncorrectedText()}
        onClick={this.handleClick}
        wordPropsFunc={this.wordPropsFunc}
        popover={
          <Popover
            activeIndex={activeIndex}
            key={activeIndex}
            studentGuess={studentGuess}
            correctAnswer={correctAnswer}
            annotation={annotation}
            onReset={this.handleActiveIndexReset}
          />
        }
      />
    );
  }
}

class Popover extends React.Component {
  static propTypes = {
    studentGuess: PropTypes.string,
    correctAnswer: PropTypes.string,
    annotation: PropTypes.string
  };

  render() {
    const {studentGuess, correctAnswer, annotation} = this.props;
    const isCorrect = studentGuess === correctAnswer;
    return (
      <PassageCorrectionPopover {...this.props}>
        <div className='passage-correction-popover__correction-wrapper'>
          {studentGuess ? (
            <StaticField
              label='Your answer'
              value={studentGuess}
              className={classnames('passage-correction-popover__student-answer', {
                'passage-correction-popover__student-answer--correct': isCorrect,
                'passage-correction-popover__student-answer--incorrect': !isCorrect
              })}
            />
          ) : null}
          <StaticField label='Correct answer' value={correctAnswer} />
        </div>
        {annotation ? (
          <div className='passage-correction-popover__annotation-wrapper'>
            <div className='a-form-input__label'>Annotation:</div>
            <MarkdownRendererV2 text={annotation} />
          </div>
        ) : null}
      </PassageCorrectionPopover>
    );
  }
}
